import { tagged } from "@mirage/service-logging";
import { scorePerson } from "@mirage/service-people/scoring/score-person";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { isTaggedPeopleResult } from "@mirage/shared/search/people-recommendations";
import { titleTokenizer } from "@mirage/shared/search/tokenizers";
import escapeRegExp from "lodash.escaperegexp";

const logger = tagged("service-typeahead-search/scoring/title-match");

type TokenizerFn = (sequence: string) => string[];

export function titleMatchScore(
  query: string,
  result: typeahead.TaggedResult,
  config: typeahead.Config,
): number {
  if (!query) return 0;

  // TODO (05/02/2025): This is a temporary fix to ensure that people filters
  // are scored correctly.
  // We should integrate displayName/email scoring for people into official
  // scoring.
  // This should proxy the title-score for people filters and people recommendations.
  if (isTaggedPeopleResult(result)) {
    return scorePerson(result.result, query);
  }

  const title = extractTitle(result);
  if (!title) return 0;

  // Growthbook flag: https://app.dropboxexperiment.com/features/dash_2024_10_25_increased_desktop_application_scoring
  if (
    config.isIncreasedDesktopApplicationScoring &&
    result.type === typeahead.ResultType.DesktopApplication
  ) {
    return desktopApplicationSimilarity(query, title);
  }

  return similarity(query, title);
}

/**
 * Similarity algos
 */

function similarity(
  query: string,
  title: string,
  tokenize: TokenizerFn = titleTokenizer,
): number {
  const queryLowercase = query.toLowerCase();
  const titleLowercase = title.toLowerCase();

  // exact match
  if (queryLowercase === titleLowercase) return 1;

  // tokenize query and title
  // TODO: evaluate removing duplicate tokens
  const qtokens = tokenize(queryLowercase);
  const ttokens = tokenize(titleLowercase);

  // query vs. title match heuristics
  const queryTokenExactMatchesScore_ = queryTokenExactMatchesScore(
    qtokens,
    ttokens,
  );
  const queryTokenPrefixMatchesScore_ = queryTokenPrefixMatchesScore(
    qtokens,
    ttokens,
  );
  const titleCoverageScore_ = titleCoverageScore(qtokens, titleLowercase);
  const firstQueryTokenOccurenceScore_ = firstQueryTokenOccurenceScore(
    qtokens,
    titleLowercase,
  );

  // weighted score
  const score =
    queryTokenExactMatchesScore_ * 0.5 +
    queryTokenPrefixMatchesScore_ * 0.2 +
    titleCoverageScore_ * 0.2 +
    firstQueryTokenOccurenceScore_ * 0.1;

  return score;
}

function desktopApplicationSimilarity(
  query: string,
  title: string,
  tokenize: TokenizerFn = titleTokenizer,
): number {
  const queryLowercase = query.toLowerCase();
  const titleLowercase = title.toLowerCase();
  const qtokens = tokenize(queryLowercase);
  const ttokens = tokenize(titleLowercase);

  const prefixMatchScore = queryTokenPrefixMatchesScore(qtokens, ttokens);

  // Tight requirements for this elevated scoring to kick in. We require every
  // token in the query to be a prefix match for tokens in the title. Otherwise
  // we fall back to normal scoring, which can still surface apps in non-p1
  // (typos, partial matches, etc.)
  if (prefixMatchScore !== 1) {
    return similarity(query, title);
  }

  // `base` vaults these apps to p1+
  //
  // 2.0 is sufficient when:
  // * Title Match Score is weight at 0.6
  // * Suggested Query Score is set to 0.6-0.7, which we must outscore for p1 (see Growthbook flag `dash_typeahead_2024_06_05_suggested_query_score`)
  //
  // Example:
  //   2.0 * 60% weight = 1.2 of the weighted Title Match Score score comes from `base`
  const base = 2.0;

  // These apps are winning already, but we need to find the best app!
  const additional = firstQueryTokenOccurenceScore(qtokens, titleLowercase);

  return base + additional;
}
// Export for testing
export { desktopApplicationSimilarity };

/**
 * Title vs. Query analysis
 */

//
// Q: what % of query tokens are contained in the title as full word matches?
// ----------------------------------------------------------------------------
function queryTokenExactMatchesScore(
  qtokens: string[],
  ttokens: string[],
): number {
  const qmatches = qtokens.filter((qtoken) => ttokens.includes(qtoken));

  // Performance - avoid math when unnecessary
  if (qmatches.length === 0) return 0.0;

  return qmatches.length / qtokens.length;
}
// Export for testing
export { queryTokenExactMatchesScore };

//
// Q: what % of query tokens are contained in the title as prefix matches?
// ----------------------------------------------------------------------------
function queryTokenPrefixMatchesScore(
  qtokens: string[],
  ttokens: string[],
): number {
  const qmatches = qtokens.filter((qtoken) =>
    ttokens.some((ttoken) => ttoken.startsWith(qtoken)),
  );

  // Performance - avoid math when unnecessary
  if (qmatches.length === 0) return 0.0;

  return qmatches.length / qtokens.length;
}
// Export for testing
export { queryTokenPrefixMatchesScore };

//
// Q: how much of the title is covered by query tokens?
// ----------------------------------------------------------------------------
function titleCoverageScore(qtokens: string[], titleLowercase: string): number {
  const remainingTitle = qtokens.reduce((remaining, qtoken) => {
    const re = new RegExp(escapeRegExp(qtoken), "g");
    return remaining.replace(re, "");
  }, titleLowercase);

  // Performance - avoid math when unnecessary
  if (remainingTitle.length === 0) return 1.0;

  return 1 - remainingTitle.length / titleLowercase.length;
}
// Export for testing
export { titleCoverageScore };

//
// Q: how soon into the title is a query token substring found?
// ----------------------------------------------------------------------------
function firstQueryTokenOccurenceScore(
  qtokens: string[],
  titleLowercase: string,
): number {
  const matchingLocations = qtokens
    .map((qtoken) => titleLowercase.indexOf(qtoken))
    .filter((index) => index >= 0);

  // Performance - avoid math when unnecessary
  if (!matchingLocations.length) return 0;

  const earliestMatch = Math.min(...matchingLocations);

  return 1 - earliestMatch / titleLowercase.length;
}
// Export for testing
export { firstQueryTokenOccurenceScore };

/**
 * Helpers
 */

function extractTitle(result: typeahead.TaggedResult): string | undefined {
  switch (result.type) {
    case typeahead.ResultType.PreviousQuery:
      return result.result.query;
    case typeahead.ResultType.SearchResult:
      return result.result.title;
    case typeahead.ResultType.Recommendation:
      return result.result.title;
    case typeahead.ResultType.Stack: {
      const stackName = result.result.stack_data?.name;
      if (stackName) {
        return stackName;
      } else {
        logger.warn(`Couldn't get title to score, Stack has no name`);
        return undefined;
      }
    }
    case typeahead.ResultType.StackItem:
      return result.result.name;
    case typeahead.ResultType.DesktopApplication:
      return result.result.title;
    case typeahead.ResultType.DesktopFile:
      return result.result.title;
    case typeahead.ResultType.DraftWithSource:
    case typeahead.ResultType.RecentChat:
    case typeahead.ResultType.DraftFromTemplate:
    case typeahead.ResultType.DraftFromTone:
      return result.result.title;
    case typeahead.ResultType.AskSuggestion:
      return result.result.text;
    case typeahead.ResultType.PeopleFilter:
    case typeahead.ResultType.PeopleRecommendation:
      return result.result.displayName ?? result.result.email;
    default: {
      result.type satisfies
        | typeahead.ResultType.CalendarEvent
        | typeahead.ResultType.URLShortcut
        | typeahead.ResultType.MathCalculation
        | typeahead.ResultType.SuggestedQuery
        | typeahead.ResultType.AskUserQuery
        | typeahead.ResultType.SearchFilter
        | typeahead.ResultType.OrganizeUserQuery
        | typeahead.ResultType.WriteUserQuery
        | typeahead.ResultType.MyProfileSuggestion;
      logger.warn(
        `Couldn't get title to score, no title for result.type ${result.type}, check title-match`,
      );
      return undefined;
    }
  }
}
